import {inject, Injectable} from '@angular/core';
import {
    BehaviorSubject, finalize, Observable, of, switchMap, take, takeWhile, tap
} from 'rxjs';
import {UrlEnum} from "app/core/types/url-enum";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {UrlService} from "./url.service";
import {
    SMMAgendaCategory,
    SMMCalendarFullSettings, SMMCalendarSettings
} from "../types/calendar.types";
import {SMMUserSelectionService} from "./smm.user.selection.service";

@Injectable({providedIn: 'root'})
export class CalendarSettingsService {
    //services
    private _httpClient = inject(HttpClient);
    private _urlService: UrlService = inject(UrlService);
    private _userSelectionService: SMMUserSelectionService = inject(SMMUserSelectionService);
    private initialized = false;

    constructor() {
        this.getSettings();
    }

    //observables
    private _fullSettings: BehaviorSubject<SMMCalendarFullSettings | null> = new BehaviorSubject(null);
    private _settingsCache: Map<string,SMMCalendarFullSettings> = new Map();
    private _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);

    get busy$() {
        return this._busy.asObservable();
    }

    get fullSettings$(): Observable<SMMCalendarFullSettings> {
        return this._fullSettings.asObservable();
    }

    public getSettings(){
        if(this.initialized){
            return;
        }
        this._userSelectionService.selectedMedic$.pipe(switchMap(u => {
            if(u.calendar)
            {
                if(this._settingsCache.has(u.calendar)){
                    return of(this._settingsCache.get(u.calendar)).pipe(tap(settings => {
                        this._fullSettings.next(settings);
                    }), finalize(() => {
                        this._busy.next(false)
                    }));
                }
                return this._httpClient.get<SMMCalendarFullSettings>(this._urlService.getUrl(UrlEnum.private_calendar_full_settings)
                    .replace("{calendarId}", u.calendar))
                    .pipe(tap(settings => {
                        this._settingsCache.set(u.calendar, settings);
                        this._fullSettings.next(settings);
                    }), finalize(() => {
                        this._busy.next(false)
                    }));
            }
            else{
                // just keep taking data
                return of(null)
            }
        })).subscribe();
        this.initialized = true;
    }

    public updateSettings(settings: SMMCalendarSettings){
        this._busy.next(true);
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        return this._httpClient.put<string>(this._urlService.getUrl(UrlEnum.private_calendar_settings).replace('{id}', settings.id), JSON.stringify(settings), {headers: headers}).pipe(tap(result => {
            const fullSettings = this._fullSettings.value;
            fullSettings.calendarSettings=settings;
            this._fullSettings.next(fullSettings);
        }), finalize(() => {
            this._busy.next(false)
        }));
    }

    public deleteCalendarCategory(categID: string){
        this._busy.next(true);
        return this._httpClient.delete<any>(this._urlService.getUrl(UrlEnum.private_calendar_categ_id).replace('{id}', categID)).pipe(tap(result => {
            const settings = this._fullSettings.value;
            settings.agendaCategories.splice(settings.agendaCategories.findIndex( c=> c.id == categID),1);
            this._fullSettings.next(settings);
        }), finalize(() => {
            this._busy.next(false)
        }));
    }

    public addCalendarCategory(smmCateg:SMMAgendaCategory){
        this._busy.next(true);
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        return this._httpClient.post<string>(this._urlService.getUrl(UrlEnum.private_calendar_categ), JSON.stringify(smmCateg), {headers: headers}).pipe(tap(result => {
            const settings = this._fullSettings.value;
            smmCateg.id = result;
            settings.agendaCategories.push(smmCateg);
            this._fullSettings.next(settings);
        }), finalize(() => {
            this._busy.next(false)
        }));

    }
}
